import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    Button,
    Drawer,
    IconButton,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
    drawerClasses,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Box, alpha } from '@mui/system';

import { useTranslate } from 'src/locales';
import { useGetOrganizationCandidateQuery, useGetProfileDocumentsQuery, useUpdateCandidateRatingMutation } from 'src/services/candidates/candidates.service';
import { paper } from 'src/theme/css';

import Iconify from 'src/components/iconify';

import CandidateProfileDocumentsView from 'src/sections/profile/documents/profile-documents';
import CandidateProfileEditor from 'src/sections/profile/profile-sections/profile-editor';

import { enqueueSnackbar } from 'notistack';
import DocumentViewer from 'src/components/documents/document-viewer';
import StarRating from 'src/components/rating/star-rating';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';
import { useAppDispatch } from 'src/store/store';
import { toggleCandidateDrawer } from 'src/store/slices/candidate/candidateSlice';
import { DocumentType } from 'src/services/files/file.types';

type Props = {
    candidateId?: string;
    open: boolean;
    onClose: () => void;
    onNext?: () => void | undefined;
    onPrevious?: () => void | undefined;
};

enum TabValue {
    PROFILE = 'profile',
    DOCUMENTS = 'documents',
    RESUME = 'resume',
}

export default function CandidateDrawer({
    candidateId,
    open,
    onClose,
    onNext,
    onPrevious
}: Props) {

    const theme = useTheme();
    const { t } = useTranslate();
    const dispatch = useAppDispatch();

    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    const [currentTab, setCurrentTab] = useState<TabValue>(TabValue.PROFILE);

    const [updateCandidateRating, { isLoading: updatingRating }] = useUpdateCandidateRatingMutation();

    const { data: candidate, isLoading: isCandidateLoading } = useGetOrganizationCandidateQuery(candidateId as string, {
        skip: !candidateId
    });

    const { data: profileDocuments } = useGetProfileDocumentsQuery(
        {
            profileId: candidate?.candidate_profile?.id as string,
            params: {
                page: 1,
                per_page: 10,
            },
        },
        { skip: !candidate?.candidate_profile?.id }
    );

    const drawerWidth = isMobile ? '100%' : 800;

    const resumeDoc = useMemo(() => profileDocuments?.results.find((doc) => doc.document.type === DocumentType.RESUME), [profileDocuments]);

    useEffect(() => {
        if (!candidate) {
            return;
        }

        if (resumeDoc) {
            setCurrentTab(TabValue.RESUME);
        }
        else {
            setCurrentTab(TabValue.PROFILE);
        }

    }, [open, candidate, setCurrentTab, resumeDoc]);

    const handleRatingChange = useCallback(async (rating: number) => {
        if (!candidateId) return;
        try {
            await updateCandidateRating({ candidateId, rating }).unwrap();

            enqueueSnackbar(t('candidates.api.update_rating.success'), { variant: 'success' });
        }
        catch (error) {
            console.error(error);

            enqueueSnackbar(t('candidates.api.update_rating.default_error'), { variant: 'error' });
        }
    }, [updateCandidateRating, t, candidateId]);

    const handleClose = useCallback(() => {
        dispatch(toggleCandidateDrawer({ status: false }));
        onClose();
    }, [dispatch, onClose]);

    const renderHead = (
        <Stack direction="column" justifyContent="start" sx={{ borderBottom: (themex) => `1px solid ${themex.palette.divider}`, py: 2, pr: 1, pl: 2.5, pt: 4 }}>
            <Stack
                direction="row"
                alignItems="start"
                justifyContent="space-between"
            >
                <Stack direction="column" alignItems="start" justifyContent="start" >
                    <TruncatedTextField
                        text={candidate?.candidate_profile ? `${candidate.candidate_profile.first_name} ${candidate.candidate_profile.last_name}` : 'Loading...'}
                        variant='h4'
                        mode='lines'
                        limit={1}
                    />

                    <Stack direction="row" alignItems="center" justifyContent="start" sx={{ color: 'text.secondary' }} spacing={2}>
                        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                            {candidate?.candidate_profile?.position_title}
                        </Typography>
                    </Stack>
                </Stack>

                <Stack
                    direction="row"
                    alignItems="start"
                    justifyContent="center"
                >
                    <Stack
                        direction="row"
                        alignItems="start"
                        justifyContent="center"
                    >
                        <Box sx={{ width: 30 }} >
                            {
                                onPrevious && (
                                    <Tooltip title={t('candidates.drawer.tooltips.previous_candidate')}>
                                        <IconButton onClick={onPrevious}>
                                            <Iconify icon="mingcute:arrow-left-fill" />
                                        </IconButton>
                                    </Tooltip>
                                )
                            }
                        </Box>

                        <Box sx={{ width: 30 }} >
                            {
                                onNext && (
                                    <Tooltip title={t('candidates.drawer.tooltips.next_candidate')}>
                                        <IconButton onClick={onNext}>
                                            <Iconify icon="mingcute:arrow-right-fill" />
                                        </IconButton>
                                    </Tooltip>
                                )
                            }
                        </Box>

                    </Stack>
                    <IconButton onClick={handleClose}>
                        <Iconify icon="mingcute:close-line" />
                    </IconButton>
                </Stack>
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mt={1} width="100%">
                <StarRating
                    rating={candidate?.rating || 0}
                    onSelectExcitementRating={(newRating) => handleRatingChange(newRating)}
                />
            </Stack>
        </Stack>
    );

    const renderTabs = (
        <Box>
            <Tabs
                value={currentTab}
                onChange={(_event, tab) => setCurrentTab(tab)}
                sx={{
                    px: 2.5,
                    boxShadow: (themex) => `inset 0 -2px 0 0 ${alpha(themex.palette.grey[500], 0.08)}`,
                }}
            >
                <Tab
                    value={TabValue.PROFILE}
                    label={t('candidates.drawer.tabs.profile')}
                />

                {
                    resumeDoc && (
                        <Tab
                            value={TabValue.RESUME}
                            label={t('candidates.drawer.tabs.resume')}
                        />
                    )
                }

                <Tab
                    value={TabValue.DOCUMENTS}
                    label={t('candidates.drawer.tabs.documents')}
                />
            </Tabs>
        </Box>
    );

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            slotProps={{
                backdrop: { invisible: true },
            }}
            PaperProps={{
                sx: {
                    width: drawerWidth
                }
            }}
            sx={{
                [`& .${drawerClasses.paper}`]: {
                    ...paper({ theme, bgcolor: theme.palette.background.default }),
                }
            }}
        >
            <Stack direction="column" justifyContent="start" sx={{ pb: 3, height: '100%' }}>

                <Stack direction="column" justifyContent="start" sx={{ top: 0, zIndex: 1, mb: 3, position: 'sticky', background: 'white' }}>
                    {renderHead}

                    {renderTabs}

                </Stack>

                {
                    currentTab === TabValue.PROFILE &&
                    <Stack sx={{ px: 3 }}>
                        <CandidateProfileEditor
                            profile={candidate?.candidate_profile}
                            isLoading={isCandidateLoading}
                            mode="view"
                            showNavigation={false}
                        />
                    </Stack>
                }

                {
                    currentTab === TabValue.DOCUMENTS &&
                    <Stack sx={{ px: 3 }}>
                        <CandidateProfileDocumentsView profileId={candidate?.candidate_profile?.id as string} uploadVisible={false} />
                    </Stack>
                }

                {
                    currentTab === TabValue.RESUME &&
                    <Stack sx={{ px: 3, flexGrow: 1 }} >
                        {resumeDoc ? (
                            <Box sx={{ height: '100%' }} >

                                <DocumentViewer fileUrl={resumeDoc.document.public_path} />

                            </Box>
                        ) : (
                            <Typography sx={{ py: 5 }} align="center" variant="body2">
                                {t('candidates.drawer.no_resume')}
                            </Typography>
                        )}
                    </Stack>
                }

            </Stack>
        </Drawer>
    );
}